// frontend/src/components/AudiencePanel.tsx
//
// Единый блок «Аудитория»: выбор среза (26 измерений в 6 группах) + таблица-рейтинг
// с реальными метриками сегмента. Источник — metrika_dimension_daily (Stat API
// Метрики): визиты, заказы, выручка, % отказов, среднее время визита. CR и средний
// чек считаются на лету. Выкуп тут не виден (его нет в этом срезе данных).

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  fetchAudience,
  AUDIENCE_DIMENSION_GROUPS,
  type AudienceDimensionKey,
  type AudienceRow,
} from "../api/analytics.js";
import { formatRub, formatInt } from "../charts/format.js";

export interface AudiencePanelProps {
  campaignId?: number | null;
  days: number;
  defaultDimension?: AudienceDimensionKey;
}

type SortKey = "visits" | "revenue" | "purchases";

function fmtDuration(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
function fmtPct0(x: number | null): string {
  return x == null || !Number.isFinite(x) ? "—" : `${(x * 100).toFixed(0)}%`;
}
function fmtPct1(x: number | null): string {
  return x == null || !Number.isFinite(x) ? "—" : `${(x * 100).toFixed(1).replace(".", ",")}%`;
}
function dimLabel(key: AudienceDimensionKey): string {
  for (const g of AUDIENCE_DIMENSION_GROUPS) {
    const it = g.items.find((i) => i.key === key);
    if (it) return it.label;
  }
  return key;
}

export function AudiencePanel({ campaignId = null, days, defaultDimension = "gender_age" }: AudiencePanelProps) {
  const [dim, setDim] = useState<AudienceDimensionKey>(defaultDimension);
  const [rows, setRows] = useState<AudienceRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<SortKey>("visits");

  useEffect(() => {
    setLoading(true);
    fetchAudience(campaignId, days, dim)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId, days, dim]);

  const totals = useMemo(() => {
    const r = rows ?? [];
    return {
      visits: r.reduce((a, x) => a + x.visits, 0),
      purchases: r.reduce((a, x) => a + x.purchases, 0),
      revenue: r.reduce((a, x) => a + x.revenue, 0),
    };
  }, [rows]);

  const sorted = useMemo(() => {
    const r = [...(rows ?? [])];
    r.sort((a, b) => (b[sort] ?? 0) - (a[sort] ?? 0));
    return r;
  }, [rows, sort]);

  const maxVisits = Math.max(...(rows ?? []).map((r) => r.visits), 1);

  const th: CSSProperties = { padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border-default)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface-card)", zIndex: 1 };
  const thR: CSSProperties = { ...th, textAlign: "right" };
  const td: CSSProperties = { padding: "6px 10px", borderBottom: "1px solid var(--border-default)", color: "var(--text-secondary)" };
  const tdR: CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" };

  const sortBtn = (key: SortKey, label: string) => (
    <button onClick={() => setSort(key)} style={{
      border: "1px solid var(--border-default)", borderRadius: 6, padding: "3px 9px", fontSize: 11, cursor: "pointer",
      background: sort === key ? "var(--brand-primary)" : "var(--surface-card)",
      color: sort === key ? "#fff" : "var(--text-secondary)", fontWeight: sort === key ? 600 : 500,
    }}>{label}</button>
  );

  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-secondary)", fontWeight: 600 }}>
          Аудитория · разбор по срезу
        </div>
        <select value={dim} onChange={(e) => setDim(e.target.value as AudienceDimensionKey)}
          style={{ padding: "5px 10px", background: "var(--surface-card)", color: "var(--text-primary)", border: "1px solid var(--border-default)", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
          {AUDIENCE_DIMENSION_GROUPS.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>сортировка:</span>
        {sortBtn("visits", "Визиты")}
        {sortBtn("revenue", "Выручка")}
        {sortBtn("purchases", "Заказы")}
        {loading && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>загрузка…</span>}
      </div>

      {sorted.length === 0 ? (
        <div style={{ color: "var(--text-muted)", fontSize: 13, padding: "8px 0" }}>
          {loading ? "Загрузка…" : "Нет данных по этому срезу за период."}
        </div>
      ) : (
        <div style={{ maxHeight: 540, overflowY: "auto", border: "1px solid var(--border-default)", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>{dimLabel(dim)}</th>
              <th style={thR}>Визиты</th>
              <th style={thR}>Заказы</th>
              <th style={thR}>CR</th>
              <th style={thR}>Ср. чек</th>
              <th style={thR}>Выручка ₽</th>
              <th style={thR}>Отказы</th>
              <th style={thR}>Ср. время</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const share = totals.visits > 0 ? r.visits / totals.visits : 0;
              const cr = r.visits > 0 ? r.purchases / r.visits : null;
              const avgCheck = r.purchases > 0 ? r.revenue / r.purchases : null;
              return (
                <tr key={r.segment}>
                  <td style={{ ...td, color: "var(--text-primary)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.segment}>{r.segment}</td>
                  <td style={tdR}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{(share * 100).toFixed(0)}%</span>
                      <span>{formatInt(r.visits)}</span>
                    </div>
                    <div style={{ height: 3, marginTop: 3, background: "var(--border-default)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(r.visits / maxVisits) * 100}%`, background: "var(--brand-primary)", opacity: 0.5 }} />
                    </div>
                  </td>
                  <td style={tdR}>{formatInt(r.purchases)}</td>
                  <td style={tdR}>{fmtPct1(cr)}</td>
                  <td style={tdR}>{avgCheck != null ? formatRub(avgCheck) : "—"}</td>
                  <td style={tdR}>{formatRub(r.revenue)}</td>
                  <td style={tdR}>{fmtPct0(r.bounce_rate)}</td>
                  <td style={tdR}>{fmtDuration(r.avg_visit_duration)}</td>
                </tr>
              );
            })}
            <tr>
              <td style={{ ...td, color: "var(--text-primary)", fontWeight: 600, borderBottom: "none" }}>Всего · {sorted.length} сегм.</td>
              <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatInt(totals.visits)}</td>
              <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatInt(totals.purchases)}</td>
              <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{totals.visits > 0 ? fmtPct1(totals.purchases / totals.visits) : "—"}</td>
              <td style={{ ...tdR, borderBottom: "none" }}>—</td>
              <td style={{ ...tdR, fontWeight: 600, borderBottom: "none" }}>{formatRub(totals.revenue)}</td>
              <td style={{ ...tdR, borderBottom: "none" }}>—</td>
              <td style={{ ...tdR, borderBottom: "none" }}>—</td>
            </tr>
          </tbody>
        </table>
        </div>
      )}
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
        Источник — Метрика (окно {days} дней). CR = заказы ÷ визиты, средний чек = выручка ÷ заказы.
        Показаны все сегменты среза, отсортированы по выбранному столбцу (список со скроллом).
        Выкуп в этом срезе не виден — он в «Продажи — итог». Сегодня исключён.
      </div>
    </div>
  );
}
